import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Printer, Send, Clock, Loader2 } from "lucide-react";
import { fmtDate } from "@/components/DatePicker";
import { toast } from "sonner";

// Shows a nudge when the active client hasn't completed MICR check setup. Calls onStatus(configured) so parents can gate printing.
export function CheckSetupNudge({ params, open, onStatus }) {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/check-setup/status", { params: params() }).then((r) => { setS(r.data); onStatus?.(r.data.configured); }).catch(() => { setS(null); onStatus?.(true); });
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line
  if (!s || s.configured) return null;
  const pending = s.form_status === "sent";
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/check-setup/send", { company_id: params().company_id || null, to_email: null }); toast.success(`Check setup form emailed to ${data.to_email}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm flex flex-wrap items-start gap-3" data-testid="check-setup-nudge">
      <Printer className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <div className="font-medium">Check printing isn't set up for this client</div>
        <div className="text-xs mt-0.5">
          {pending ? <><Clock className="w-3 h-3 inline mr-1" />Setup form sent to {s.form_to} on {fmtDate(s.form_sent_at?.slice(0, 10))} — waiting for their bank details and signature.</>
            : "You can still record the check, but MICR checks can't be printed until the client completes the one-time bank & signature form."}
        </div>
      </div>
      {user.role === "bureau_admin"
        ? <Button size="sm" variant="outline" className="border-amber-400 bg-white hover:bg-amber-100" data-testid="check-setup-nudge-send" disabled={busy} onClick={send}>{busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} {pending ? "Resend setup form" : "Send setup form"}</Button>
        : <Button size="sm" variant="outline" className="border-amber-400 bg-white hover:bg-amber-100" asChild data-testid="check-setup-nudge-link"><Link to="/check-printing">Open Check Printing</Link></Button>}
    </div>
  );
}
